import { DatePipe, DecimalPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { translate, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AssetService, IAssetModel } from '@libs/vc-asset-lib';
import {
    AssetOriginType,
    AssetTypes,
    AssetTypesHelper,
    DateTimeInterval,
    DateUtil,
    Day,
    DayHelper,
    IFacetNodeModel,
    IKeyValueGeneric,
    RiskDashboardCtaService,
    selectTranslate,
    Severity,
    TimeInterval,
    TimeIntervalHelper,
    UserContextService,
    WhoAmI,
} from '@libs/vc-core-lib';
import { EventService, IDashboardModel, IDateCountMapModel } from '@libs/vc-event-lib';
import { FindingService, IFindingDashboardResultsModel } from '@libs/vc-finding-lib';
import {
    ChartWrapperComponent,
    EventsFindingsChartComponent,
    FindingsByCategoryChartComponent,
    FindingsBySeverityChartComponent,
    IEventsFindingData,
    PieChartComponent,
} from '@libs/vc-shared-ui-lib';
import { PointClickEventObject, SeriesClickEventObject } from 'highcharts';
import { isEmpty } from 'lodash-es';
import { finalize, Subject, takeUntil } from 'rxjs';
import {
    ButtonComponent,
    ChartEmptyComponent,
    IconComponent,
    LinkComponent,
    NotificationService,
    SelectComponent,
} from 'vcp-common-ui-lib';

@Component({
    selector: 'vc-landing',
    templateUrl: './landing.component.html',
    styleUrls: ['./landing.component.scss'],
    providers: [DatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslocoPipe,
        ChartWrapperComponent,
        ChartEmptyComponent,
        EventsFindingsChartComponent,
        LinkComponent,
        DecimalPipe,
        PieChartComponent,
        FindingsBySeverityChartComponent,
        FindingsByCategoryChartComponent,
        SelectComponent,
        IconComponent,
        ButtonComponent,
    ],
})
export class LandingComponent implements OnInit, OnDestroy {
    private readonly _destroy$: Subject<void> = new Subject<void>();
    private readonly _assetService: AssetService = inject(AssetService);
    private readonly _findingService: FindingService = inject(FindingService);
    private readonly _eventService: EventService = inject(EventService);
    private readonly _datePipe: DatePipe = inject(DatePipe);
    private readonly _router: Router = inject(Router);
    private readonly _notificationService: NotificationService = inject(NotificationService);
    private readonly _transloco: TranslocoService = inject(TranslocoService);
    private readonly _destroyRef: DestroyRef = inject(DestroyRef);
    private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly _userContextService: UserContextService = inject(UserContextService);
    private readonly _riskDashboardCtaService: RiskDashboardCtaService = inject(RiskDashboardCtaService);

    toDate!: string;
    dateTimeInterval!: string;
    assetsTotalCount!: number;
    findingsBySeverityTimeFrame: TimeInterval | null = TimeInterval.WEEK;
    findingsByCategoryTimeFrame: TimeInterval | null = TimeInterval.WEEK;
    intervals = [TimeInterval.DAY, TimeInterval.WEEK, TimeInterval.MONTH];
    riskiestAssets: Array<IAssetModel> = [];
    assetsByType: { id: string; name: string; y: number; color: string }[] = [];
    findingsBySeverityFacets: IFacetNodeModel[] = [];
    findingsByCategoryFacets: IFacetNodeModel[] = [];
    assetCountLoader: boolean = false;
    assetsByTypeLoader: boolean = false;
    riskiestAssetLoader: boolean = false;
    findingChartLoader: boolean = false;
    eventFindingChartLoader: boolean = false;
    eventsFindingData: IEventsFindingData | null = null;
    showRiskDashboardCta = false;

    readonly pieChartLegends = {
        symbolHeight: 8,
        symbolWidth: 8,
        symbolRadius: 4,
        itemMarginBottom: 4,
    };

    landingChartType: ILandingChartType = {
        riskiestAsset: TimeInterval.MONTH,
        findingsByCategories: TimeInterval.WEEK,
        findingsBySeverity: TimeInterval.WEEK,
        eventFindingChart: TimeInterval.WEEK,
    };

    constructor() {
        selectTranslate(this._transloco, () => this._rerenderAssetsByTypeChart(), this._destroyRef);
    }

    ngOnInit(): void {
        this._userContextService
            .getContext()
            .pipe(takeUntil(this._destroy$))
            .subscribe((user: WhoAmI | null) => {
                this.showRiskDashboardCta = this._riskDashboardCtaService.isVisible(user);
                this._changeDetectorRef.markForCheck();
            });
        this.fetchAssetsTotalCount();
        this.fetchAssetsByType();
        this.fetchRiskiestAssets();
        this.fetchFindingCharts(TimeInterval.WEEK);
    }

    openRiskDashboard(): void {
        this._riskDashboardCtaService.openNewExperience();
    }

    fetchAssetsTotalCount(): void {
        this.assetCountLoader = true;
        this._assetService
            .getAssetsTotalCount(this._setFromDate(TimeInterval.MONTH), this.toDate)
            .pipe(
                takeUntil(this._destroy$),
                finalize(() => {
                    this.assetCountLoader = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: (value: number) => {
                    this.assetsTotalCount = value;
                },
                error: () => {
                    this._notificationService.error(translate('LANDING.DASHBOARD.ASSET_COUNT.FETCH_ERROR'));
                },
            });
    }

    fetchAssetsByType(): void {
        this.assetsByType = [];
        this.assetsByTypeLoader = true;
        this._assetService
            .getAssetsCountByType(this._setFromDate(TimeInterval.MONTH), this.toDate)
            .pipe(
                takeUntil(this._destroy$),
                finalize(() => {
                    this.assetsByTypeLoader = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: (value: IKeyValueGeneric) => {
                    this._renderAssetsByTypeChart(value);
                },
                error: () => {
                    this._notificationService.error(translate('LANDING.DASHBOARD.ASSETS_BY_TYPE.FETCH_ERROR'));
                },
            });
    }

    private _renderAssetsByTypeChart(assetsByTypeResults: IKeyValueGeneric): void {
        const assetByTypeColor: Map<string, string> = new Map<string, string>()
            .set(AssetTypes.NETWORK, 'var(--primary-200)')
            .set(AssetTypes.HOST, 'var(--secondary-700)')
            .set(AssetTypes.ENDPOINT, 'var(--primary-300)')
            .set(AssetTypes.APPLICATION, 'var(--primary-500)')
            .set(AssetTypes.USER, 'var(--primary-400)')
            .set(AssetTypes.UTM, 'var(--secondary-300)')
            .set(AssetTypes.SITE, 'var(--secondary-50)');
        const data: { id: string; name: string; y: number; color: string }[] = [];
        Object.entries(assetsByTypeResults)
            .sort(([, a], [, b]) => Number(b) - Number(a))
            .forEach(([value, count]) => {
                data.push({
                    id: value,
                    name: translate(`CORE.ASSET_TYPES.${value}`),
                    y: Number(count),
                    color: assetByTypeColor.get(value) ?? '',
                });
            });
        this.assetsByType = data;
    }

    private _rerenderAssetsByTypeChart(): void {
        this.assetsByType = this.assetsByType.map((asset) => {
            asset.name = translate(`CORE.ASSET_TYPES.${asset.id}`);
            return asset;
        });
        this._changeDetectorRef.markForCheck();
    }

    fetchRiskiestAssets(): void {
        this.riskiestAssetLoader = true;
        this.riskiestAssets = [];
        this._assetService
            .getsRiskiestAssets(this._setFromDate(this.landingChartType.riskiestAsset), this.toDate)
            .pipe(
                takeUntil(this._destroy$),
                finalize(() => {
                    this.riskiestAssetLoader = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: (assets: Array<IAssetModel>) => {
                    this.riskiestAssets = assets;
                },
                error: () => {
                    this._notificationService.error(translate('LANDING.DASHBOARD.RISKIEST_ASSETS.FETCH_ERROR'));
                },
            });
    }

    fetchFindingsBySeverityChart(timeInterval: TimeInterval | null) {
        this.landingChartType.findingsBySeverity = timeInterval;
        this.fetchFindingCharts(this.landingChartType.findingsBySeverity);
    }

    fetchFindingsByCategoriesChart(timeInterval: TimeInterval | null) {
        this.landingChartType.findingsByCategories = timeInterval;
        this.fetchFindingCharts(this.landingChartType.findingsByCategories);
    }

    fetchFindingCharts(timeInterval: TimeInterval | null): void {
        if (!timeInterval) return;

        this.findingChartLoader = true;
        this._findingService
            .getDashboardResults(this._setFromDate(timeInterval), this.toDate, this.dateTimeInterval)
            .pipe(
                takeUntil(this._destroy$),
                finalize(() => {
                    this.findingChartLoader = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: (facetedFindings: IFindingDashboardResultsModel) => {
                    this._buildFindingCharts(facetedFindings, timeInterval);
                },
                error: () => {
                    this._notificationService.error(translate('LANDING.DASHBOARD.FINDING.FETCH_ERROR'));
                },
            });
    }

    private _buildFindingCharts(facets: IFindingDashboardResultsModel, timeInterval: TimeInterval): void {
        if (this.landingChartType.findingsBySeverity === timeInterval) {
            this._renderFindingsBySeverityChart(facets.bySeverity);
        }
        if (this.landingChartType.findingsByCategories === timeInterval) {
            this.findingsByCategoryFacets = Object.values(facets.byCategory).map(({ key, value }) => ({
                value: key,
                count: value,
                childFacet: [],
            }));
        }
    }

    private _renderFindingsBySeverityChart(facets: Map<Severity, number>): void {
        this.findingsBySeverityFacets = [];

        if (!isEmpty(facets)) {
            this.findingsBySeverityFacets = Object.entries(facets).map(([key, value]) => ({
                value: key,
                count: Number(value),
                childFacet: [],
            }));
        }
    }

    fetchEventFindingChart(timeInterval: TimeInterval | null): void {
        if (!timeInterval) return;

        this.eventsFindingData = null;
        this.eventFindingChartLoader = true;
        const fromDate = this._setFromDate(timeInterval);

        this._eventService
            .getDashboardResult(fromDate, this.toDate, this.dateTimeInterval)
            .pipe(
                takeUntil(this._destroy$),
                finalize(() => {
                    this.eventFindingChartLoader = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: (dashboardResult: IDashboardModel) => {
                    if (dashboardResult && !isEmpty(dashboardResult.dateCountMapsByGroup)) {
                        this.renderEventsFindingsChart(fromDate, dashboardResult.dateCountMapsByGroup, timeInterval);
                    }
                },
                error: () => {
                    this._notificationService.error(translate('LANDING.DASHBOARD.EVENT.FETCH_ERROR'));
                },
            });
    }

    private renderEventsFindingsChart(
        from: string,
        dateCountMapsByGroup: IDateCountMapModel[],
        timeInterval: TimeInterval
    ): void {
        const eventsData: number[] = [];
        const findingsData: number[] = [];
        const categories: string[] = [];

        for (
            let i = DateUtil.getUtcDate(from);
            i <= DateUtil.getUtcDate(this.toDate);
            timeInterval === TimeInterval.DAY ? i.setHours(i.getHours() + 1) : i.setDate(i.getDate() + 1)
        ) {
            const dateCondition =
                timeInterval === TimeInterval.DAY
                    ? this._datePipe.transform(i, 'yyyy-MM-ddTHH') + ':00:00Z'
                    : this._datePipe.transform(i, 'yyyy-MM-dd') + 'T00:00:00Z';
            const matchingCount = dateCountMapsByGroup.find((count) => count.date === dateCondition);
            if (matchingCount) {
                eventsData.push(matchingCount.eventCountByGroup['EVENTS'] ?? 0);
                findingsData.push(matchingCount.eventCountByGroup['ESCALATIONS'] ?? 0);
            } else {
                eventsData.push(0);
                findingsData.push(0);
            }

            categories.push(dateCondition);
        }

        this.eventsFindingData = { eventsData, findingsData, categories };
    }

    onEventsFindingsSeriesClick(event: SeriesClickEventObject): void {
        const fromDate = new Date(event.point.category);
        const toDate = new Date(fromDate);
        if (this.landingChartType.eventFindingChart === TimeInterval.DAY) {
            toDate.setHours(toDate.getHours() + 1);
        } else {
            toDate.setDate(toDate.getDate() + 1);
        }

        if (event.point.series.name === translate('LANDING.CHART.TITLE.EVENTS')) {
            this.navigateToEventsSearch(fromDate.toISOString(), toDate.toISOString());
        } else if (event.point.series.name === translate('LANDING.CHART.TITLE.FINDINGS')) {
            this.navigateToFindingsSearch(fromDate.toISOString(), toDate.toISOString());
        }
    }

    async navigateToFindingsSearch(fromDate: string, toDate: string) {
        await this._router.navigate([`/finding/search`], {
            queryParams: {
                fromInclusive: fromDate,
                toExclusive: toDate,
            },
        });
    }

    async navigateToEventsSearch(fromDate: string, toDate: string) {
        await this._router.navigate([`event/search`], {
            queryParams: {
                timestampAfter: fromDate,
                timestampBefore: toDate,
                chartClickEvent: true,
            },
        });
    }
    async onRiskiestAssetClick(id: string) {
        await this._router.navigate([`asset/view/details`], {
            queryParams: { assetId: id, assetOriginType: AssetOriginType.ASSET_LANDING },
        });
    }

    assetTypeItemRenderer = (type: string): string => AssetTypesHelper.getAssetTypeLocale(type as AssetTypes);
    assetTypeIconRenderer = (type: string): string => AssetTypesHelper.getAssetTypeIcon(type as AssetTypes);

    eventsFindingCategoryFormatter = (value: string): string => {
        return this._getCategoryName(this.landingChartType.eventFindingChart as TimeInterval, new Date(value));
    };

    get eventsFindingXAxisTitle(): string {
        switch (this.landingChartType.eventFindingChart) {
            case TimeInterval.DAY:
                return translate('CORE.HOUR');
            case TimeInterval.WEEK:
                return translate('CORE.DAY');
            case TimeInterval.MONTH:
                return translate('COMMON_UI.DATE');
            default:
                return '';
        }
    }

    private readonly _getCategoryName = (timeInterval: TimeInterval, date: Date): string => {
        switch (timeInterval) {
            case TimeInterval.DAY:
                return this._datePipe.transform(date, 'H') ?? '';
            case TimeInterval.WEEK:
                return DayHelper.getDayLocale((this._datePipe.transform(date, 'EEEE')?.toUpperCase() as Day) ?? '');
            case TimeInterval.MONTH:
                return date.getDate().toString();
            default:
                return '';
        }
    };

    private _setFromDate(sortBy: TimeInterval | null): string {
        if (!sortBy) return '';

        this.toDate = new Date().toISOString();
        const fromDate = new Date(this.toDate);
        switch (sortBy) {
            case TimeInterval.DAY:
                fromDate.setHours(fromDate.getHours() - 23);
                this.dateTimeInterval = DateTimeInterval.HOUR;
                break;
            case TimeInterval.WEEK:
                fromDate.setDate(fromDate.getDate() - 6);
                this.dateTimeInterval = DateTimeInterval.DAY;
                break;
            case TimeInterval.MONTH:
                fromDate.setMonth(fromDate.getMonth() - 1);
                fromDate.setDate(fromDate.getDate() + 1);
                this.dateTimeInterval = DateTimeInterval.DAY;
                break;
        }
        return fromDate.toISOString();
    }

    navigateToAssetSearch() {
        this._router.navigate(['/asset/search']);
    }

    navigateToFindingsBySeverity = (event: PointClickEventObject) => {
        this._router.navigate(['/finding/search'], {
            queryParams: {
                fromInclusive: this._setFromDate(this.landingChartType.findingsBySeverity),
                toExclusive: this.toDate,
                severity: event.point.name,
            },
        });
    };

    navigateToFindingsByCategory = (event: SeriesClickEventObject) => {
        this._router.navigate(['/finding/search'], {
            queryParams: {
                fromInclusive: this._setFromDate(this.landingChartType.findingsByCategories),
                toExclusive: this.toDate,
                category: event.point.category,
            },
        });
    };

    navigateToAssetByType = (event: PointClickEventObject) => {
        this._router.navigate(['/asset/search'], {
            queryParams: {
                type: event.point.options.id,
                showBackLink: true,
            },
        });
    };

    intervalsItemRenderer = (timeInterval: TimeInterval): string =>
        TimeIntervalHelper.getTimeIntervalLocale(timeInterval);

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }
}

interface ILandingChartType {
    riskiestAsset: TimeInterval | null;
    findingsBySeverity: TimeInterval | null;
    findingsByCategories: TimeInterval | null;
    eventFindingChart: TimeInterval | null;
}
